/**
 * Independent validation of generated questions.
 *
 * The governing rule: never trust a generator because the same code produced
 * both the question and its answer. Validation re-derives correctness through
 * separate code paths and rejects anything that is not provably sound. A
 * generator is expected to call the relevant validator and regenerate (with a
 * new seed) on failure, so an invalid question can never reach a student.
 */

import { Rational } from "./rational";
import { LinearEquation, type SolutionCategory } from "./linear";
import { parseEquation } from "./parse-equation";

export interface ValidationResult {
  readonly ok: boolean;
  /** Human-readable failure reasons; empty when ok. */
  readonly errors: readonly string[];
}

export function valid(): ValidationResult {
  return { ok: true, errors: [] };
}

export function invalid(...errors: string[]): ValidationResult {
  return { ok: false, errors };
}

export function combine(
  ...results: readonly ValidationResult[]
): ValidationResult {
  const errors: string[] = [];
  for (const r of results) errors.push(...r.errors);
  return errors.length === 0 ? valid() : { ok: false, errors };
}

/** Bounds a generator declares for a linear-equation problem instance. */
export interface EquationValidationSpec {
  /** The claimed solution category. */
  readonly expectedCategory: SolutionCategory;
  /** The claimed solution value (required iff expectedCategory === "one"). */
  readonly expectedValue?: Rational;
  /**
   * Maximum absolute integer magnitude allowed for any coefficient or
   * constant appearing in the assembled equation. Guards against ugly,
   * oversized numbers slipping through.
   */
  readonly maxAbsMagnitude?: bigint;
  /**
   * If true, the one-solution value must be an integer. Used when a
   * difficulty level or calibration profile forbids fractional answers.
   */
  readonly requireIntegerSolution?: boolean;
}

/**
 * Verify a linear equation independently of how it was constructed.
 *
 * Checks, in order:
 *  - the independently computed category matches the claim;
 *  - for a one-solution claim, the independently computed value matches AND
 *    the equation is actually satisfied by it (both sides evaluated fresh);
 *  - integer-solution and magnitude constraints, when specified.
 */
export function validateEquation(
  eq: LinearEquation,
  spec: EquationValidationSpec,
): ValidationResult {
  const errors: string[] = [];
  const solution = eq.solve();

  if (solution.category !== spec.expectedCategory) {
    errors.push(
      `category mismatch: generator claimed "${spec.expectedCategory}" but solver found "${solution.category}"`,
    );
  }

  if (spec.expectedCategory === "one") {
    if (!spec.expectedValue) {
      errors.push('expectedValue is required when expectedCategory is "one"');
    } else if (solution.category === "one" && solution.value) {
      if (!solution.value.equals(spec.expectedValue)) {
        errors.push(
          `value mismatch: generator claimed ${spec.expectedValue.toString()} but solver found ${solution.value.toString()}`,
        );
      }
      // Independent satisfaction check: evaluate both sides at the value.
      if (!eq.isSatisfiedBy(spec.expectedValue)) {
        errors.push(
          `equation is not satisfied by the claimed value ${spec.expectedValue.toString()}`,
        );
      }
      if (spec.requireIntegerSolution && !solution.value.isInteger()) {
        errors.push(
          `solution ${solution.value.toString()} is not an integer but an integer was required`,
        );
      }
    }
  }

  if (spec.maxAbsMagnitude !== undefined) {
    const parts: Array<[string, Rational]> = [
      ["left coefficient", eq.left.coeff],
      ["left constant", eq.left.constant],
      ["right coefficient", eq.right.coeff],
      ["right constant", eq.right.constant],
    ];
    for (const [label, r] of parts) {
      const magOk =
        absBig(r.n) <= spec.maxAbsMagnitude &&
        r.d <= spec.maxAbsMagnitude;
      if (!magOk) {
        errors.push(
          `${label} ${r.toString()} exceeds max magnitude ${spec.maxAbsMagnitude.toString()}`,
        );
      }
    }
  }

  return errors.length === 0 ? valid() : { ok: false, errors };
}

/**
 * Presentation cross-check. Given the displayed form of a question and a
 * function that parses it back into a LinearEquation, confirm the reparsed
 * equation is mathematically equivalent to the internal one (same solution).
 *
 * The parser is injected so this module stays free of parsing concerns; the
 * real parser is wired in once the normalizer exists.
 */
export function crossCheckEquationPresentation(
  internal: LinearEquation,
  displayed: string,
  reparse: (text: string) => LinearEquation = parseEquation,
): ValidationResult {
  let reparsed: LinearEquation;
  try {
    reparsed = reparse(displayed);
  } catch (e) {
    return invalid(
      `presentation could not be reparsed: ${(e as Error).message}`,
    );
  }
  const a = internal.solve();
  const b = reparsed.solve();
  if (a.category !== b.category) {
    return invalid(
      `presentation cross-check: category differs (internal "${a.category}" vs displayed "${b.category}")`,
    );
  }
  if (a.category === "one" && b.category === "one") {
    if (!a.value || !b.value || !a.value.equals(b.value)) {
      return invalid(
        `presentation cross-check: solution differs (internal ${a.value?.toString()} vs displayed ${b.value?.toString()})`,
      );
    }
  }
  return valid();
}

function absBig(x: bigint): bigint {
  return x < 0n ? -x : x;
}
